import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getAuthToken, refreshSession } from '../api';

/**
 * En enda socket för hela appen.
 *
 * Tidigare öppnade kartan och chatten var sin anslutning. Servern kunde
 * bara hålla en socket per användare, så när den ena stängdes slutade den
 * andra fungera. Nu delas anslutningen av alla komponenter.
 */
const SocketContext = createContext({ socket: null, connected: false });

export function SocketProvider({ children }) {
  const { token } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!token) {
      setSocket(null);
      setConnected(false);
      return undefined;
    }

    // Token läses vid varje anslutningsförsök i stället för att frysas här,
    // så att en återanslutning efter en förnyelse använder den nya.
    const instance = io({ auth: (cb) => cb({ token: getAuthToken() }) });

    instance.on('connect', () => setConnected(true));
    instance.on('disconnect', () => setConnected(false));

    instance.on('connect_error', (err) => {
      setConnected(false);
      // Servern nekar handskakningen när åtkomsttoken gått ut. Förnya och
      // försök igen i stället för att lämna användaren utan realtid.
      if (/session|token|inloggad/i.test(err.message || '')) {
        refreshSession()
          .then(() => instance.connect())
          .catch(() => {});
      }
    });

    setSocket(instance);

    return () => {
      instance.removeAllListeners();
      instance.close();
    };
  }, [token]);

  return (
    <SocketContext.Provider value={{ socket, connected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
